import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import _ from 'lodash';
import { apiLambda } from '../services/apiLambda';

export interface IConsult {
  nome_consulta: string;
  campos: {
    descricao: string;
    conteudo: string;
  }[];
  debitos: {
    ipva: IDebt[];
    licenciamento: IDebt[];
    dpvat: IDebt[];
    multas: IDebt[];
    outros: IDebt[];
  };
  pagarTudo: boolean;
  mensagemEntrada: string;
  mensagemSaida: string;
  codigo: number;
}

interface InstallmentsMath {
  times: number;
  total: number;
  interest: number;
  cet: number;
}

export type DebtType = 'ipva' | 'dpvat' | 'multas' | 'outros' | 'licenciamento';

export type DebtTypeWithLicensing =
  | 'ipva'
  | 'dpvat'
  | 'multas'
  | 'outros'
  | 'licenciamento';

export interface IDebt {
  codigo: number;
  descricao: string;
  descricaoAdicional: string;
  emissor: string;
  expired: boolean;
  grupo: number;
  validade: string;
  valor: number;
  vencimento: string;
  disable?: boolean;
  ano?: number;
  unica?: boolean;
  quantidadeAno?: number;
  onCheck: {
    select: number[];
    unSelect: number[];
  };
  onUnCheck: {
    select: number[];
    unSelect: number[];
  };
}

interface InstallmentInfo {
  month: number;
  installmentPrice: any;
  totalPrice: string;
  checked: boolean;
  discount: number;
  cet: number;
}

export interface PixInfo {
  month: number;
  installmentPrice: string;
  totalPrice: string;
  checked: boolean;
  discount: number;
  cet: number;
}

interface IResponseInstallments {
  tipo: string;
  parcelas: number;
  cet: number;
  valor: number;
  total: number;
  desconto: number;
}

interface IVehicleData {
  id: number;
  placa: string;
  renavam?: string;
  uf: string;
  municipio?: string;
  chassi?: string;
  tipo?: string;
  marca?: string;
  ano?: number;
  importado?: number;
}

const CUPOM_SPAN_REGEX = /(<span hidden="">)(.*)(<\/span>)/;

interface PaymentContextData {
  handleDebtsSelectionAllByType(debtType: DebtType, isInitial?: boolean): void;
  handleDebtsSelection(debtType: DebtTypeWithLicensing, data: IDebt): void;
  getDebts(data: IConsult): Promise<void>;
  handleInstallments(data: InstallmentsMath): InstallmentInfo;
  handleSelectInstallment(installment: InstallmentInfo): void;
  handleSelectPix(): void;
  handleSetConsults(consults: IConsult[]): void;
  handleSetCupom(cupom: string): void;
  ipva: IDebt[];
  licenciamento: IDebt[];
  dpvat: IDebt[];
  multas: IDebt[];
  outros: IDebt[];
  ipvaSelected: IDebt[];
  licenciamentoSelected: IDebt[];
  dpvatSelected: IDebt[];
  multasSelected: IDebt[];
  outrosSelected: IDebt[];
  setOutrosSelected(debts: IDebt[]): void;
  ipvaTotal: number;
  licenciamentoTotal: number;
  dpvatTotal: number;
  multasTotal: number;
  outrosTotal: number;
  installments: InstallmentInfo[];
  pix: PixInfo;
  consultCode: number;
  consults: IConsult[];
  cupomStatus: boolean;
  vehicle: IVehicleData;
  client: string;
  cupom: string;
  handleSetVehicle(vehicle: IVehicleData): void;
  handleSetClient(client: string): void;
  entryMessage: string;
  exitMessage: string;
  handleSetConsultCOde(code: number): void;
  installmentsLoading: boolean;
  setReady(ready: boolean): void;
  clear(): void;
  clearInstallments(): void;
  consultName: string;
  owner: string;
  renavam: string;
  isPix: boolean;
  handleSetIsPix(bool: boolean): void;
  setRecovery(bool: boolean): void;
  pixTax: number;
  availableCoupon: string;
  ready: boolean;
}

const PaymentContext = createContext<PaymentContextData>(
  {} as PaymentContextData,
);

export const PaymentsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [ipva, setIPVA] = useState<IDebt[]>([]);
  const [licenciamento, setLicenciamento] = useState<IDebt[]>([]);
  const [dpvat, setDPVAT] = useState<IDebt[]>([]);
  const [multas, setMultas] = useState<IDebt[]>([]);
  const [outros, setOutros] = useState<IDebt[]>([]);

  const [ipvaSelected, setIPVASelected] = useState<IDebt[]>([]);
  const [licenciamentoSelected, setLicenciamentoSelected] = useState<IDebt[]>(
    [],
  );
  const [dpvatSelected, setDPVATSelected] = useState<IDebt[]>([]);
  const [multasSelected, setMultasSelected] = useState<IDebt[]>([]);
  const [outrosSelected, setOutrosSelected] = useState<IDebt[]>([]);

  const [ipvaTotal, setIpvaTotal] = useState(0);
  const [licenciamentoTotal, setLicenciamentoTotal] = useState(0);
  const [dpvatTotal, setDpvatTotal] = useState(0);
  const [multasTotal, setMultasTotal] = useState(0);
  const [outrosTotal, setOutrosTotal] = useState(0);

  const [consultCode, setConsultCode] = useState(0);
  const [isPayAll, setIsPayAll] = useState(false);

  const [cupom, setCupom] = useState('');
  const [cupomStatus, setCupomStatus] = useState(false);

  const [availableCoupon, setAvaliableCoupon] = useState('');

  const [entryMessage, setEntryMessage] = useState('');
  const [exitMessage, setExitMessage] = useState('');

  const [consults, setConsults] = useState<IConsult[]>([]);

  const [installments, setInstallments] = useState<InstallmentInfo[]>([]);
  const [pix, setPix] = useState<PixInfo>({} as PixInfo);
  const [isPix, setIsPix] = useState<boolean>(false);

  const [vehicle, setVehicle] = useState<IVehicleData>({} as IVehicleData);

  const [client, setClient] = useState('');

  const [consultName, setConsultName] = useState('');

  const [installmentsLoading, setLoading] = useState(false);

  const [ready, setReady] = useState(false);
  const [recovery, setRecovery] = useState(false);

  const [checkConsultName, setCheckConsultName] = useState(false);

  const [owner, setOwner] = useState('');
  const [renavam, setRenavam] = useState('');

  const handleSetConsults = useCallback((consults: IConsult[]) => {

    localStorage.setItem('@Zignet:Consultas', JSON.stringify(consults));

    setConsults(consults);
  }, []);

  // # Define na primeira renderização:
  // * Estados de débitos selecionados a partir do local storage
  // * Estados referente a consulta e opções de pagamento a partir do local storage
  useEffect(() => {
    const ipvaOnStorage = localStorage.getItem('@Zignet:ipvaSelected');
    const multasOnStorage = localStorage.getItem('@Zignet:multasSelected');
    const dpvatOnStorage = localStorage.getItem('@Zignet:dpvatSelected');
    const licenciamentoOnStorage = localStorage.getItem(
      '@Zignet:licenciamentoSelected',
    );
    const outrosOnStorage = localStorage.getItem('@Zignet:outrosSelected');

    const ipvaSelectedOnStorage: IDebt[] = ipvaOnStorage
      ? JSON.parse(ipvaOnStorage)
      : [];
    const multasSelectedOnStorage: IDebt[] = multasOnStorage
      ? JSON.parse(multasOnStorage)
      : [];
    const dpvatSelectedOnStorage: IDebt[] = dpvatOnStorage
      ? JSON.parse(dpvatOnStorage)
      : [];
    const licenciamentoSelectedOnStorage: IDebt[] = licenciamentoOnStorage
      ? JSON.parse(licenciamentoOnStorage)
      : [];
    const outrosSelectedOnStorage: IDebt[] = outrosOnStorage
      ? JSON.parse(outrosOnStorage)
      : [];

    setIPVASelected(ipvaSelectedOnStorage);
    setMultasSelected(multasSelectedOnStorage);
    setDPVATSelected(dpvatSelectedOnStorage);
    setLicenciamentoSelected(licenciamentoSelectedOnStorage);
    setOutrosSelected(outrosSelectedOnStorage);

    const consultsOnStorage = localStorage.getItem('@Zignet:Consultas');
    const installmentsOnStorage = localStorage.getItem('@Zignet:parcelas');
    const vehicleOnStorage = localStorage.getItem('@Zignet:vehicle');
    const clientOnStorage = localStorage.getItem('@Zignet:client');
    const cupomStatusOnStorage = localStorage.getItem('@Zignet:cupomStatus');
    const cupomOnStorage = localStorage.getItem('@Zignet:cupom');
    const pixOnStorage = localStorage.getItem('@Zignet:pix');

    const consultsFromStorage: IConsult[] = consultsOnStorage
      ? JSON.parse(consultsOnStorage)
      : [];

    const installmentsFromStorage: InstallmentInfo[] = installmentsOnStorage
      ? JSON.parse(installmentsOnStorage)
      : [];

    const vehicleFromStorage: IVehicleData = vehicleOnStorage
      ? JSON.parse(vehicleOnStorage)
      : {};

    const clientFromStorage: string = clientOnStorage
      ? JSON.parse(clientOnStorage)
      : '';

    const cupomStatusFromStorage: boolean =
      cupomStatusOnStorage && cupomStatusOnStorage !== 'undefined'
        ? JSON.parse(cupomStatusOnStorage)
        : false;

    const cupomFromStorage: string = cupomOnStorage
      ? JSON.parse(cupomOnStorage)
      : '';

    const pixFromStorage: PixInfo = pixOnStorage
      ? JSON.parse(pixOnStorage)
      : {};

    setConsults(consultsFromStorage);
    setInstallments(installmentsFromStorage);
    setVehicle(vehicleFromStorage);
    setClient(clientFromStorage);
    setCupomStatus(cupomStatusFromStorage ?? false);
    setCupom(cupomFromStorage);
    setPix(pixFromStorage);

    if (pixFromStorage?.checked) {
      setIsPix(true);
    }
  }, []);

  // # Define a cada renderização e atualização das dependências:
  // * Storage de débitos selecionados a partir do Estado Local
  // * Storage referente a consulta e opções de pagamento a partir do Estado Local
  useEffect(() => {
    localStorage.setItem('@Zignet:ipvaSelected', JSON.stringify(ipvaSelected));
    localStorage.setItem(
      '@Zignet:multasSelected',
      JSON.stringify(multasSelected),
    );
    localStorage.setItem(
      '@Zignet:dpvatSelected',
      JSON.stringify(dpvatSelected),
    );
    localStorage.setItem(
      '@Zignet:licenciamentoSelected',
      JSON.stringify(licenciamentoSelected),
    );
    localStorage.setItem(
      '@Zignet:outrosSelected',
      JSON.stringify(outrosSelected),
    );

    localStorage.setItem('@Zignet:cupom', JSON.stringify(cupom));
    localStorage.setItem('@Zignet:cupomStatus', JSON.stringify(cupomStatus));
    localStorage.setItem('@Zignet:ipva', JSON.stringify(ipva));
    localStorage.setItem('@Zignet:multas', JSON.stringify(multas));
    localStorage.setItem('@Zignet:dpvat', JSON.stringify(dpvat));
    localStorage.setItem(
      '@Zignet:licenciamento',
      JSON.stringify(licenciamento),
    );
    localStorage.setItem('@Zignet:outros', JSON.stringify(outros));
  }, [
    cupom,
    cupomStatus,
    dpvat,
    dpvatSelected,
    ipva,
    ipvaSelected,
    licenciamento,
    licenciamentoSelected,
    multas,
    multasSelected,
    outrosSelected,
    outros,
  ]);

  const handleSetAvailableCoupon = (entryMessageToCheckCoupon: string) => {
    const groups = CUPOM_SPAN_REGEX.exec(entryMessageToCheckCoupon);
    if (groups) {
      setAvaliableCoupon(groups[2].trim());
    }
  };

  const getDebts = useCallback(async (data: IConsult, storage = false) => {
    // console.log('🚀 ~ file: payments.tsx:405 ~ getDebts ~ data:', data);
    if (!storage) {
      localStorage.removeItem('@Zignet:ipvaSelected');
      localStorage.removeItem('@Zignet:multasSelected');
      localStorage.removeItem('@Zignet:dpvatSelected');
      localStorage.removeItem('@Zignet:licenciamentoSelected');
      localStorage.removeItem('@Zignet:outrosSelected');

      localStorage.removeItem('@Zignet:parcelas');
      localStorage.removeItem('@Zignet:pix');
      localStorage.removeItem('@Zignet:cupom');
      localStorage.removeItem('@Zignet:cupomStatus');

      localStorage.removeItem('@Zignet:consulta');
      localStorage.removeItem('@Zignet:client');

      setIPVASelected([]);
      setMultasSelected([]);
      setDPVATSelected([]);
      setLicenciamentoSelected([]);
      setOutrosSelected([]);

      setInstallments([]);
      setPix({} as PixInfo);
      setCupom('');
      setCupomStatus(false);
    }

    const consult: IConsult = JSON.parse(
      localStorage.getItem('@Zignet:consulta')!,
    );

    setReady(false);

    if (consult) {
      if (consult.codigo !== data?.codigo) {
        localStorage.removeItem('@Zignet:multasSelected');
        localStorage.removeItem('@Zignet:ipvaSelected');
        localStorage.removeItem('@Zignet:dpvatSelected');
        localStorage.removeItem('@Zignet:outrosSelected');
        localStorage.removeItem('@Zignet:cupom');
        localStorage.removeItem('@Zignet:cupomStatus');
        setCupom('');
        setOwner('');
        setMultasSelected([]);
        setIPVASelected([]);
        setDPVATSelected([]);
        setOutrosSelected([]);
      }
    }

    const parser = new DOMParser();
    const entryMessageElement = data?.mensagemEntrada
      ? parser.parseFromString(data?.mensagemEntrada, 'text/html')
      : null;
    // const isPartnerFlow = localStorage.getItem('@partnerPrismicData');

    // let generalElementEntryMessage = null;
    // let partnerElementEntryMessage = null;
    let zigFrotasElementEntryMessage = null;

    if (entryMessageElement) {
      // generalElementEntryMessage =
      //   entryMessageElement?.getElementById('general') ||
      //   entryMessageElement?.getElementById('GENERAL');
      // partnerElementEntryMessage =
      //   entryMessageElement?.getElementById('partner') ||
      //   entryMessageElement?.getElementById('PARTNER');
      zigFrotasElementEntryMessage =
        entryMessageElement?.getElementById('frotas') ||
        entryMessageElement?.getElementById('FROTAS');
    }

    // if (isPartnerFlow && partnerElementEntryMessage) {
    //   setEntryMessage(partnerElementEntryMessage.innerHTML);
    //   handleSetAvailableCoupon(partnerElementEntryMessage.innerHTML);
    // } else if (!isPartnerFlow && zigFrotasElementEntryMessage) {
    //   setEntryMessage(zigFrotasElementEntryMessage.innerHTML);
    //   handleSetAvailableCoupon(zigFrotasElementEntryMessage.innerHTML);
    // } else if (generalElementEntryMessage) {
    //   setEntryMessage(generalElementEntryMessage.innerHTML);
    //   handleSetAvailableCoupon(generalElementEntryMessage.innerHTML);
    // } else if (
    //   !generalElementEntryMessage &&
    //   !partnerElementEntryMessage &&
    //   !zigFrotasElementEntryMessage &&
    //   data?.mensagemEntrada
    // ) {
    //   setEntryMessage(data?.mensagemEntrada);
    //   handleSetAvailableCoupon(data?.mensagemEntrada);
    // }

    if (zigFrotasElementEntryMessage) {
      setEntryMessage(zigFrotasElementEntryMessage.innerHTML);
      handleSetAvailableCoupon(zigFrotasElementEntryMessage.innerHTML);
    } else if (
      // !generalElementEntryMessage &&
      // !partnerElementEntryMessage &&
      !zigFrotasElementEntryMessage &&
      data?.mensagemEntrada
    ) {
      setEntryMessage(data?.mensagemEntrada);
      handleSetAvailableCoupon(data?.mensagemEntrada);
    }

    const exitMessageElement = data?.mensagemSaida
      ? parser.parseFromString(data?.mensagemSaida, 'text/html')
      : null;

    // let generalElementExitMessage = null;
    // let partnerElementExitMessage = null;
    let zigFrotasElementExitMessage = null;
    if (exitMessageElement) {
      // generalElementExitMessage =
      //   exitMessageElement?.getElementById('general') ||
      //   exitMessageElement?.getElementById('GENERAL');
      // partnerElementExitMessage =
      //   exitMessageElement?.getElementById('partner') ||
      //   exitMessageElement?.getElementById('PARTNER');
      zigFrotasElementExitMessage =
        exitMessageElement?.getElementById('frotas') ||
        exitMessageElement?.getElementById('FROTAS');
    }

    // if (isPartnerFlow && partnerElementExitMessage) {
    //   setExitMessage(partnerElementExitMessage.innerHTML);
    // } else if (!isPartnerFlow && zigFrotasElementExitMessage) {
    //   setExitMessage(zigFrotasElementExitMessage.innerHTML);
    // } else if (generalElementExitMessage) {
    //   setExitMessage(generalElementExitMessage.innerHTML);
    // } else if (
    //   !generalElementExitMessage &&
    //   !partnerElementExitMessage &&
    //   !zigFrotasElementExitMessage &&
    //   data?.mensagemSaida
    // ) {
    //   setExitMessage(data?.mensagemSaida);
    // }
    
    if (zigFrotasElementExitMessage) {
      setExitMessage(zigFrotasElementExitMessage.innerHTML);
    } else if (
      !zigFrotasElementExitMessage &&
      data?.mensagemSaida
    ) {
      setExitMessage(data?.mensagemSaida);
    }

    setConsultCode(data?.codigo);

    setIPVA(data?.debitos?.ipva || []);
    setLicenciamento(data?.debitos?.licenciamento || []);
    setDPVAT(data?.debitos?.dpvat || []);
    setMultas(data?.debitos?.multas || []);
    setOutros(data?.debitos?.outros || []);

    setIsPayAll(data?.pagarTudo ?? false);

    setOwner(
      data?.campos?.find((c) => c.descricao === 'PROPRIETARIO')?.conteudo || '',
    );

    setRenavam(
      data?.campos?.find((c) => c.descricao === 'RENAVAM')?.conteudo || '',
    );

    setConsultName(data?.nome_consulta);

    setReady(true);

    localStorage.setItem('@Zignet:consulta', JSON.stringify(data));
  }, []);

  useEffect(() => {
    const consultOnStorage = localStorage.getItem('@Zignet:consulta');

    const consult: IConsult | undefined = consultOnStorage
      ? JSON.parse(consultOnStorage)
      : undefined;

    if (consult && recovery) {
      getDebts(consult, recovery);
    }
  }, [getDebts, recovery]);

  const handleSetVehicle = useCallback((vehicle: IVehicleData) => {
    localStorage.setItem('@Zignet:vehicle', JSON.stringify(vehicle));
    setVehicle(vehicle);
  }, []);

  const handleSetClient = useCallback((client: string) => {
    localStorage.setItem('@Zignet:client', JSON.stringify(client));
    setClient(client);
  }, []);

  const handleSetConsultCOde = useCallback((code: number) => {
    setConsultCode(code);
    localStorage.removeItem('@Zignet:outrosSelected');
    localStorage.removeItem('@Zignet:consulta');
    localStorage.removeItem('@Zignet:Consultas');
    localStorage.removeItem('@Zignet:multasSelected');
    localStorage.removeItem('@Zignet:ipvaSelected');
    localStorage.removeItem('@Zignet:dpvatSelected');
    localStorage.removeItem('@Zignet:outrosSelected');
    localStorage.removeItem('@Zignet:licenciamentoSelected');
    localStorage.removeItem('@Zignet:parcelas');
    localStorage.removeItem('@Zignet:pix');
    localStorage.removeItem('@Zignet:vehicle');
    localStorage.removeItem('@Zignet:client');
    localStorage.removeItem('@Zignet:cupom');
    localStorage.removeItem('@Zignet:cupomStatus');
    setOutrosSelected([]);
  }, []);

  const switchDebtType = useCallback(
    (debtType: DebtTypeWithLicensing) => {
      let state: IDebt[] = [...ipva];
      let stateSelected: IDebt[] = [...ipvaSelected];
      let setState = setIPVASelected;

      switch (debtType) {
        case 'ipva':
          state = [...ipva];
          stateSelected = [...ipvaSelected];
          setState = setIPVASelected;
          break;
        case 'licenciamento':
          state = [...licenciamento];
          stateSelected = [...licenciamentoSelected];
          setState = setLicenciamentoSelected;
          break;
        case 'dpvat':
          state = [...dpvat];
          stateSelected = [...dpvatSelected];
          setState = setDPVATSelected;
          break;
        case 'multas':
          state = [...multas];
          stateSelected = [...multasSelected];
          setState = setMultasSelected;
          break;
        case 'outros':
          state = [...outros];
          stateSelected = [...outrosSelected];
          setState = setOutrosSelected;
          break;
      }

      return { state, stateSelected, setState };
    },
    [
      dpvat,
      dpvatSelected,
      ipva,
      ipvaSelected,
      licenciamento,
      licenciamentoSelected,
      multas,
      multasSelected,
      outros,
      outrosSelected,
    ],
  );

  const toggleDebtsSelection = useCallback(
    (listCheck: number[], listUnCheck: number[]) => {
      const debtTypes = ['ipva', 'licenciamento', 'dpvat', 'multas', 'outros'];
      const allChecks = [...listCheck, ...listUnCheck];

      for (const type of debtTypes) {
        const { state, stateSelected, setState } = switchDebtType(
          type as DebtTypeWithLicensing,
        );

        const shouldSelect = state.some((x) => allChecks.includes(x.codigo));

        if (!shouldSelect) {
          continue;
        }

        const newSelection = [...stateSelected];

        for (const debt of state) {
          const currentSelected = newSelection.some(
            (ns) => ns.codigo == debt.codigo,
          );

          const inCheckList = listCheck.some((item) => debt.codigo == item);

          const inUnCheckList = listUnCheck.some((item) => debt.codigo == item);

          if (!inCheckList && !inUnCheckList && !currentSelected) {
            continue;
          }

          if (inUnCheckList && currentSelected) {
            const removeIndex = newSelection.findIndex(
              (item) => item.codigo == debt.codigo,
            );

            if (removeIndex != -1) newSelection.splice(removeIndex, 1);
          }

          if (inCheckList && !currentSelected) {
            newSelection.push({ ...debt });
          }
        }

        setState([...newSelection]);
      }
    },
    [switchDebtType],
  );

  const handleDebtsSelection = useCallback(
    (debtType: DebtTypeWithLicensing, data: IDebt) => {
      if (data?.disable) return;
      let newData = data;

      const { state, stateSelected, setState } = switchDebtType(debtType);

      let newStateSelected = stateSelected;

      if (debtType === 'outros') {
        const checkDebtOnState = newStateSelected.find(
          (s) => s.codigo === data.codigo,
        );
        if (!checkDebtOnState) {
          const stateSelectCode = newStateSelected.map((s) => s.codigo);
          newData = {
            ...data,
            onCheck: { ...data.onCheck, select: stateSelectCode },
          };
          const newState = [newData, ...newStateSelected];
          setState(newState);
          //setOutros(newState);
          newStateSelected = newState;
        }
      }
      if (isPayAll) {
        if (debtType === 'ipva' && newStateSelected.length) {
          return;
        } else if (newStateSelected.length !== state.length) {
          setState([...state]);
        } else {
          return;
        }
      }
      const currentSelected = newStateSelected.some(
        (ns) => ns.codigo == newData.codigo,
      );

      const listCheck = currentSelected
        ? [...data.onUnCheck.select]
        : [newData.codigo, ...newData.onCheck.select];

      const listUnCheck = currentSelected
        ? [newData.codigo, ...newData.onUnCheck.unSelect]
        : [...newData.onCheck.unSelect];

      toggleDebtsSelection(listCheck, listUnCheck);
    },
    [isPayAll, switchDebtType, toggleDebtsSelection],
  );

  const handleDebtsSelectionAllByType = useCallback(
    (debtType: DebtType, isInitial = false) => {
      const { state, stateSelected, setState } = switchDebtType(debtType);
      if (isPayAll || isInitial) {
        if (stateSelected.length !== state.length) {
          if (debtType === 'ipva') {
            const ipvaUnica = state.filter((s) => s.unica);
            const ipvaParcelas = state.filter((s) => !s.unica);
            ipvaParcelas.forEach((s) => {
              handleDebtsSelection(debtType, s);
            });

            ipvaUnica.forEach((s) => {
              handleDebtsSelection(debtType, s);
            });
          } else if (debtType === 'licenciamento') {
            state.forEach((l) => {
              handleDebtsSelection(debtType, l);
            });
          } else {
            setState(state);
          }
        }
        return;
      }

      const isNotSelect = stateSelected.length === 0;

      const listCheck: number[] = [];
      const listUnCheck: number[] = [];

      if (isNotSelect) {
        if (debtType === 'ipva') {
          const ipvaUnica = state.filter((s) => s.unica);
          const ipvaParcelas = state.filter((s) => !s.unica);

          ipvaParcelas.forEach((s) => {
            handleDebtsSelection(debtType, s);
          });

          ipvaUnica.forEach((s) => {
            handleDebtsSelection(debtType, s);
          });

          return;
        } else {
          state.forEach((s) => {
            listCheck.push(...[s.codigo, ...s.onCheck.select]);
            listUnCheck.push(...[...s.onCheck.unSelect]);
          });
        }
      } else {
        state.forEach((s) =>
          listUnCheck.push(...[s.codigo, ...s.onUnCheck.unSelect]),
        );
      }

      toggleDebtsSelection(listCheck, listUnCheck);
    },
    [switchDebtType, isPayAll, toggleDebtsSelection, handleDebtsSelection],
  );

  const handleGetInstallments = useCallback(async () => {
    setLoading(true);
    const total =
      ipvaTotal + licenciamentoTotal + outrosTotal + multasTotal + dpvatTotal;

    if (total === 0) {
      setInstallments([]);
      setPix({} as PixInfo);
      setLoading(false);
      return;
    }

    if (consultCode === 0) {
      setLoading(false);
      return;
    }

    const response = await apiLambda.get(
      `/comunix/installments/${consultCode}`,
      {
        params: {
          valor: total,
          cupom,
        },
      },
    );

    const cumpomStatusLSStr = localStorage.getItem('@Zignet:cupomStatus') ?? '';

    const cupomStatusLocalStorage = JSON.parse(
      !cumpomStatusLSStr || cumpomStatusLSStr === 'undefined'
        ? 'false'
        : cumpomStatusLSStr,
    );

    if (cupomStatusLocalStorage) {
      setCupomStatus(cupomStatusLocalStorage);
    }

    setCupomStatus(response?.data?.data?.cupomAplicado);

    if (cupom && !response?.data?.data?.cupomAplicado) {
      setCupom('');
      return;
    }

    const installments: IResponseInstallments[] = response.data.data.parcelas;

    let checkedPix = false;
    let parcela = 12;

    const pix = JSON.parse(localStorage.getItem('@Zignet:pix')!);

    if (pix) {
      checkedPix = pix?.checked;
      setIsPix(checkedPix);
    }

    const parcelas = JSON.parse(localStorage.getItem('@Zignet:parcelas')!);

    if (parcelas) {
      parcela = parcelas.find((p: any) => p?.checked)?.month;
    }

    const newInstallments: InstallmentInfo[] = installments
      .filter((i) => i?.tipo === 'C')
      .filter((i, n) => i?.parcelas <= 12)
      .map((i) => ({
        month: i.parcelas,
        installmentPrice: String(i.valor),
        totalPrice: String(i.total),
        discount: i.desconto,
        checked: checkedPix ? false : i.parcelas === parcela ? true : false,
        cet: i.cet,
      }))
      .sort((a, b) => {
        return a.month < b.month ? 1 : -1;
      });

    const pixInstallments: PixInfo[] = installments
      ?.filter((i) => i.tipo === 'P')
      ?.map((i) => ({
        month: i?.parcelas,
        installmentPrice: String(i?.valor),
        totalPrice: String(i?.total),
        discount: i?.desconto,
        checked: checkedPix ? true : false,
        cet: i?.cet,
      }));

    setLoading(false);

    setInstallments(newInstallments);
    localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));

    if (pixInstallments.length > 0) {
      setPix(pixInstallments?.[0]);
      localStorage.setItem('@Zignet:pix', JSON.stringify(pixInstallments?.[0]));
    }
  }, [
    ipvaTotal,
    licenciamentoTotal,
    outrosTotal,
    multasTotal,
    dpvatTotal,
    consultCode,
    cupom,
  ]);

  const handleInstallments = useCallback(
    ({ total, interest, times, cet }: InstallmentsMath): InstallmentInfo => {
      const installment =
        total /
        (((1 + interest) ** times - 1) / ((1 + interest) ** times * interest));
      return {
        month: times,
        installmentPrice: String(installment),
        totalPrice: String(installment * times),
        discount: 0,
        checked: false,
        cet: cet,
      };
    },
    [],
  );

  const handleSelectInstallment = useCallback(
    (installment: InstallmentInfo) => {
      const newInstallments = installments.map((i) => ({
        ...i,
        checked: i.month === installment.month ? true : false,
      }));
      setInstallments(newInstallments);
      localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));

      if (!_.isEmpty(pix)) {
        const newPix = {
          ...pix,
          checked: false,
        };
        setPix(newPix);
        localStorage.setItem('@Zignet:pix', JSON.stringify(newPix));
      }
      isPix && setIsPix(false);
    },
    [installments, pix, isPix],
  );

  const handleSelectPix = useCallback(() => {
    const newPix = {
      ...pix,
      checked: true,
    };
    setPix(newPix);
    localStorage.setItem('@Zignet:pix', JSON.stringify(newPix));

    const newInstallments = installments.map((i) => ({
      ...i,
      checked: false,
    }));
    setInstallments(newInstallments);
    localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));
    !isPix && setIsPix(true);
  }, [installments, pix, isPix]);

  const handleSetCupom = useCallback((cupom: string) => {
    setCupom(cupom);
  }, []);

  const handleSetIsPix = useCallback((bool: boolean) => {
    setIsPix(bool);
  }, []);

  useEffect(() => {
    const result =
      consultName.toLocaleLowerCase().search('licenciamento') > -1 ||
      consultName.toLocaleLowerCase().search('transfe') > -1 ||
      vehicle.uf === 'AM' ||
      vehicle.uf === 'MS' ||
      vehicle.uf === 'PA' ||
      (vehicle.uf === 'PB' &&
        consultName.toLocaleLowerCase().search('todos') > -1);

    setCheckConsultName(result);
  }, [consultName, vehicle.uf]);

  useEffect(() => {
    if (ready) {
      handleGetInstallments();
    }
  }, [handleGetInstallments, ready]);

  useEffect(() => {
    const total = ipvaSelected.reduce((a, b) => {
      return a + b.valor;
    }, 0);

    setIpvaTotal(total);
  }, [ipvaSelected]);

  useEffect(() => {
    const total = licenciamentoSelected?.reduce((a, b) => {
      return a + b?.valor;
    }, 0);

    setLicenciamentoTotal(total);
  }, [licenciamentoSelected]);

  useEffect(() => {
    const total = dpvatSelected.reduce((a, b) => {
      return a + b.valor;
    }, 0);

    setDpvatTotal(total);
  }, [dpvatSelected]);

  useEffect(() => {
    const total = multasSelected.reduce((a, b) => {
      return a + b.valor;
    }, 0);

    setMultasTotal(total);
  }, [multasSelected]);

  useEffect(() => {
    const total = outrosSelected.reduce((a, b) => {
      return a + b.valor;
    }, 0);

    setOutrosTotal(total);
  }, [outrosSelected]);

  const clear = useCallback(() => {
    setIPVASelected([]);
    setIPVA([]);
    localStorage.removeItem('@Zignet:ipvaSelected');
    setMultasSelected([]);
    setMultas([]);
    localStorage.removeItem('@Zignet:multasSelected');
    setLicenciamentoSelected([]);
    setLicenciamento([]);
    localStorage.removeItem('@Zignet:licenciamentoSelected');
    setOutrosSelected([]);
    setOutros([]);
    localStorage.removeItem('@Zignet:outrosSelected');
    setDPVATSelected([]);
    setDPVAT([]);
    localStorage.removeItem('@Zignet:dpvatSelected');
    setInstallments([]);
    localStorage.removeItem('@Zignet:parcelas');

    localStorage.removeItem('@Zignet:cupom');
    setCupom('');
    localStorage.removeItem('@Zignet:cupomStatus');
    setCupomStatus(false);

    localStorage.removeItem('@Zignet:consulta');
    localStorage.removeItem('@Zignet:Consultas');
    setConsults([]);
    localStorage.removeItem('@Zignet:vehicle');
    setVehicle({} as IVehicleData);
    localStorage.removeItem('@Zignet:client');
    localStorage.removeItem('@zignet:ConsultTimeStamp');
    setClient('');
  }, []);

  const clearInstallments = useCallback(() => {
    setIPVASelected([]);
    setIPVA([]);
    localStorage.removeItem('@Zignet:ipvaSelected');
    setMultasSelected([]);
    setMultas([]);
    localStorage.removeItem('@Zignet:multasSelected');
    setLicenciamentoSelected([]);
    setLicenciamento([]);
    localStorage.removeItem('@Zignet:licenciamentoSelected');
    setOutrosSelected([]);
    setOutros([]);
    localStorage.removeItem('@Zignet:outrosSelected');
    setDPVATSelected([]);
    setDPVAT([]);
    localStorage.removeItem('@Zignet:dpvatSelected');
    setInstallments([]);
    localStorage.removeItem('@Zignet:parcelas');
    setPix({} as PixInfo);
    localStorage.removeItem('@Zignet:pix');

    localStorage.removeItem('@Zignet:cupom');
    setCupom('');
    localStorage.removeItem('@Zignet:cupomStatus');
    setCupomStatus(false);

    localStorage.removeItem('@Zignet:consulta');
  }, []);

  const pixTax = useMemo(
    () =>
      Number(pix?.totalPrice) -
      (dpvatTotal + ipvaTotal + licenciamentoTotal + multasTotal + outrosTotal),
    [
      dpvatTotal,
      ipvaTotal,
      licenciamentoTotal,
      multasTotal,
      outrosTotal,
      pix?.totalPrice,
    ],
  );

  return (
    <PaymentContext.Provider
      value={{
        owner,
        installmentsLoading,
        handleDebtsSelection,
        getDebts,
        handleInstallments,
        handleDebtsSelectionAllByType,
        handleSelectInstallment,
        ipva,
        licenciamento,
        dpvat,
        multas,
        ipvaSelected,
        licenciamentoSelected,
        dpvatSelected,
        multasSelected,
        ipvaTotal,
        licenciamentoTotal,
        dpvatTotal,
        multasTotal,
        installments,
        consultCode,
        consults,
        handleSetConsults,
        outros,
        outrosSelected,
        setOutrosSelected,
        outrosTotal,
        handleSetCupom,
        cupomStatus,
        handleSetVehicle,
        vehicle,
        entryMessage,
        cupom,
        handleSetClient,
        client,
        exitMessage,
        handleSetConsultCOde,
        setReady,
        ready,
        clear,
        clearInstallments,
        consultName,
        pix,
        handleSelectPix,
        isPix,
        handleSetIsPix,
        pixTax,
        renavam,
        setRecovery,
        availableCoupon,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export function usePayment(): PaymentContextData {
  const context = useContext(PaymentContext);

  if (!context) {
    throw new Error('usePayment must be used within an PaymentProvider');
  }

  return context;
}
